<script>
export default {
  name: "Academics",
};
</script>

<script setup>
import { computed, defineEmits, defineProps, ref, unref } from "vue";
import { useVModel } from "@vueuse/core";
import { useVuelidate } from "@vuelidate/core";
import { helpers, requiredIf } from "@vuelidate/validators";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import BasicSwitch from "@/components/shared/input/BasicSwitch.vue";
import RadioYesNo from "@/components/shared/input/RadioYesNo.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import BasicCheckbox from "@/components/shared/input/BasicCheckbox.vue";
import VSelectCaret from "@/components/shared/select/VSelectCaret.vue";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import ClassesSelectionModal from "@/components/program-manager/sessions/components/Academics/ClassesSelectionModal.vue";
import FromLibrary from "@/components/program-manager/sessions/components/Academics/FromLibrary.vue";
import ConfiguratorWidget from "@/components/program-manager/sessions/components/ConfiguratorWidget";
import { updateFieldToDecimals } from "@/services/utils";
import Magnifier from "@/components/shared/icons/Magnifier";
import {
  assessmentTypes,
  classesTaughtTimesOptions,
  daysOfTheWeekTaught,
  entityTypes,
  sessionStatus,
} from "@/components/program-manager/sessions/constants";
import { checkIfProgramSessionTypeIsInternshipOnly } from "@/components/program-manager/sessions/utils.js";
import {
  useGetRequiredMajorOptions,
  useGetEntities,
  debounceSearchWrapper,
} from "@/components/program-manager/sessions/composable";
import WYSIWYG from "@/components/forms/SharedComponents/WYSIWYG/WYSIWYG.vue";

const props = defineProps({
  modelValue: {
    type: Object,
  },
  isSessionOwner: {
    type: Boolean,
  },
});
const emit = defineEmits(["update:modelValue"]);
if (!props?.modelValue?.academic_description) props.modelValue.academic_description = {};
const programSession = useVModel(props, "modelValue", emit);

const hasProgramSessionTypeInternshipOnly = computed(() =>
  checkIfProgramSessionTypeIsInternshipOnly(unref(programSession))
);

const rules = {
  // academic_theme: {},
  credits: {},
  academic_min_credits: {},
  academic_max_credits: {},
  academic_additional_courses_allowed: {},
  academic_max_additional_credits: {
    required: helpers.withMessage(
      "Max courses is required",
      requiredIf(function () {
        return (
          unref(programSession)?.status_id === sessionStatus.openEnrollment &&
          unref(programSession)?.academic_additional_courses_allowed &&
          !hasProgramSessionTypeInternshipOnly.value &&
          !unref(noMaxCourse)
        );
      })
    ),
  },
  specific_class_taught_time: {},
  academics_days_of_week: {},
  academic_assessment_type_id: {},
  academics_has_add_drop_period: {},
  language_entry_exam_min_score: {},
  language_entry_exam_administration: {},
  language_entry_exam_difference: {},
  transcript_provider: {},
  one_credit_add_on: {
    required: helpers.withMessage(
      "One credit add on is required to open enrollment",
      requiredIf(function () {
        return (
          unref(programSession)?.status_id === sessionStatus.openEnrollment &&
          unref(oneClassAddOn)
        );
      })
    ),
  },
  three_credit_add_on: {
    required: helpers.withMessage(
      "Three credit add on is required to open enrollment",
      requiredIf(function () {
        return (
          unref(programSession)?.status_id === sessionStatus.openEnrollment &&
          unref(threeClassAddOn)
        );
      })
    ),
  },
  classes_educational_fee: {
    required: helpers.withMessage(
      "Classes Educational Fee is required to open enrollment",
      requiredIf(function () {
        return (
          unref(programSession)?.status_id === sessionStatus.openEnrollment &&
          !!unref(programSession)?.class_filter_criteria
        );
      })
    ),
  },
};

const v$ = useVuelidate(rules, programSession, {
  $registerAs: "Academics",
  $lazy: true,
});

const noMaxCourse = ref(false);
const updateNoMaxCourse = (val) => {
  noMaxCourse.value = val;
  if (val) {
    programSession.value.academic_max_additional_credits = undefined;
  }
};
const updateAcademicAdditionalCoursesAllowed = (val) => {
  programSession.value.academic_additional_courses_allowed = val;
  if (val === false) {
    programSession.value.academic_max_additional_credits = undefined;
  }
};

const noDesignatedClasses = ref(false);

const onNoDesignatedClasses = () => {
  noDesignatedClasses.value = !noDesignatedClasses.value;
  clearClassFilters();
  if (noDesignatedClasses.value === true) {
    programSession.value.academic_additional_courses_allowed = false;
    programSession.value.academic_max_additional_credits = undefined;
  }
};

const noDesignatedLanguagePlacementExam = ref(false);
const onNoDesignatedLanguagePlacementExam = () => {
  noDesignatedLanguagePlacementExam.value = !noDesignatedLanguagePlacementExam.value;
};

const oneClassAddOn = ref(!!unref(programSession)?.one_credit_add_on);
const updateOneClassAddon = (val) => {
  oneClassAddOn.value = val;
  if (!val) {
    unref(programSession).one_credit_add_on = undefined;
  }
};
const threeClassAddOn = ref(!!unref(programSession)?.three_credit_add_on);
const updateThreeClassAddon = (val) => {
  threeClassAddOn.value = val;
  if (!val) {
    unref(programSession).three_credit_add_on = undefined;
  }
};

const classesSelectionModalOpen = ref(false);
const updateClassesFilters = (updatedClassFilters) => {
  programSession.value.class_filter_criteria = updatedClassFilters;
};
const clearClassFilters = () => {
  programSession.value.class_filter_criteria = undefined;
};

const openClassesFilterModal = () => {
  classesSelectionModalOpen.value = true;
};

const {
  execute: executeFetchRequiredMajorOptions,
  state: requiredMajorOptions,
  isLoading: requiredMajorOptionsLoading,
} = useGetRequiredMajorOptions();

const fetchRequiredMajorOptions = debounceSearchWrapper(
  executeFetchRequiredMajorOptions,
  250
);

const {
  execute: executeGetEntities,
  state: entitiesOptions,
  isLoading: entitiesLoading,
} = useGetEntities(
  { immediate: true, throwError: false, resetOnExecute: true },
  {
    extraParams: {
      account_types: [
        entityTypes.home_institution,
        entityTypes.host_institution,
      ],
    },
  }
);

const fetchEntitiesOptions = debounceSearchWrapper(executeGetEntities, 250, {
  extraParams: {
    account_types: [entityTypes.home_institution, entityTypes.host_institution],
  },
});
</script>

<template>
  <ExpandingSection field-label="Academics" class="col-span-2">
    <template #content>
      <div class="grid grid-cols-2 gap-x-24 gap-y-4">
        <div v-if="!noDesignatedClasses" class="col-span-2">
          <!-- TODO: This might come back in the future
            <SearchableSelect
            v-model="programSession.academic_theme"
            :clear-search-on-blur="() => false"
            :deselect-from-dropdown="true"
            :loading="requiredMajorOptionsLoading"
            :options="requiredMajorOptions"
            :vuelidate-instance="programSession.status_id"
            field-label="Academic Theme"
            label="cip_title"
            option-key="cip_code"
            @search="fetchRequiredMajorOptions"
          /> -->
          <CheckboxSelect
            v-model="programSession.cip_code_focuses"
            :vuelidate-instance="v$.cip_code_focuses"
            outer-classes="col-span-2 mt-4"
            label="cip_title"
            option-key="cip_code"
            field-label="Academic Theme"
            :multiple="true"
            :close-on-select="false"
            :components="{ OpenIndicator: Magnifier }"
            :options="requiredMajorOptions"
            :loading="requiredMajorOptionsLoading"
            :deselect-from-dropdown="true"
            :clear-search-on-blur="() => false"
            @search="fetchRequiredMajorOptions"
          >
          </CheckboxSelect>
        </div>
        <div class="col-span-2">
          <label class="common-label-text">
            Academic Description
          </label>
          <WYSIWYG
            data-testid="academic_description"
            v-model="programSession.academic_description"
            :character-limit="2000"
          />
        </div>
        <BasicSwitch
          class="col-span-2 border-gray-300 border-t-[1px] pt-4"
          field-label="No designated classes"
          @update:modelValue="onNoDesignatedClasses"
        />
        <div
          class="flex justify-between items-center col-span-2 border-gray-300 border-b-[1px] pb-4"
        >
          <div class="common-label-text">Classes</div>
          <div v-if="!noDesignatedClasses">
            <ClassesSelectionModal
              v-if="classesSelectionModalOpen"
              :terms="props.modelValue.terms"
              :host-institution="
                props.modelValue.session_hosting_institutions[0]?.entity
              "
              :open="classesSelectionModalOpen"
              :original-filters="props.modelValue.class_filter_criteria"
              @update-filters="updateClassesFilters"
              @close-modal="() => (classesSelectionModalOpen = false)"
            />
            <PlusIcon
              class="mr-2 text-teal-900 w-8 h-8 cursor-pointer"
              @click="openClassesFilterModal"
            />
          </div>
        </div>
        <FromLibrary
          v-if="programSession.class_filter_criteria"
          :filters="programSession.class_filter_criteria"
          @edit-filters="() => (classesSelectionModalOpen = true)"
          @clear-filters="clearClassFilters"
        />
        <ConfiguratorWidget
          v-if="programSession.class_filter_criteria"
          v-model="programSession.session_class_selection_rules.value"
          class="py-4"
          :filters="programSession.class_filter_criteria"
          :show-label="true"
        />
        <BasicTextInput
          v-if="props.isSessionOwner"
          v-model="programSession.classes_educational_fee"
          input-classes="h-10 w-full form-input"
          :vuelidate-instance="v$.classes_educational_fee"
          type="number"
          step="0.01"
          min="0"
          outer-classes="col-span-2 flex flex-wrap dollar-input items-center mt-2"
          @blur="
            () =>
              updateFieldToDecimals('classes_educational_fee', programSession)
          "
        >
          <template #fieldLabel>
            <div class="common-label-text w-full">
              <span
                v-if="programSession.class_filter_criteria"
                class="text-red-100 pr-2"
                >**</span
              >Classes Educational Fee
            </div>
            <span class="common-label-text mr-2">$</span>
          </template>
        </BasicTextInput>

        <RadioYesNo
          v-if="!noDesignatedClasses"
          v-model="programSession.exact_credits"
          class="col-span-1 pb-3"
          field-label="Credits"
          no-label="Range"
          yes-label="Exact Number"
        />
        <div
          v-if="!noDesignatedClasses"
          class="col-span-1 grid grid-cols-2 gap-x-8 items-end"
        >
          <BasicTextInput
            v-if="!programSession.exact_credits"
            v-model="programSession.academic_min_credits"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.academic_min_credits"
            type="number"
            step=".01"
            min="0"
            outer-classes="col-span-1 flex items-center"
            @blur="
              () =>
                updateFieldToDecimals('academic_min_credits', programSession)
            "
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2">Min</span>
            </template>
          </BasicTextInput>

          <BasicTextInput
            v-if="!programSession.exact_credits"
            v-model="programSession.academic_max_credits"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.academic_max_credits"
            type="number"
            step=".01"
            min="0"
            outer-classes="col-span-1 flex items-center"
            @blur="
              () =>
                updateFieldToDecimals('academic_max_credits', programSession)
            "
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2">Max</span>
            </template>
          </BasicTextInput>

          <BasicTextInput
            v-if="programSession.exact_credits"
            v-model="programSession.academic_max_credits"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.academic_max_credits"
            type="number"
            step="1"
            min="0"
            outer-classes="col-span-2 flex items-center"
            @blur="
              () => {
                updateFieldToDecimals('academic_max_credits', programSession);
                updateFieldToDecimals('academic_min_credits', programSession);
              }
            "
            @update:modelValue="
              (val) => {
                programSession.academic_max_credits = val;
                programSession.academic_min_credits = val;
              }
            "
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2">Credits</span>
            </template>
          </BasicTextInput>
        </div>
        <RadioYesNo
          v-if="!noDesignatedClasses"
          v-model="programSession.academic_additional_courses_allowed"
          class="col-span-1 pb-3"
          field-label="Can student add additional courses?"
          @update:model-value="updateAcademicAdditionalCoursesAllowed"
        />
        <div
          v-if="!noDesignatedClasses"
          v-show="programSession.academic_additional_courses_allowed"
          class="col-span-1 grid grid-cols-2 gap-x-8 pt-6 items-baseline"
        >
          <BasicCheckbox
            v-model="noMaxCourse"
            input-classes="inline-block"
            outer-classes="col-span-1 flex items-center h-10"
            field-label="No Max"
            field-label-classes="text-base font-normal"
            @update:modelValue="updateNoMaxCourse"
          />

          <BasicTextInput
            v-if="!noMaxCourse"
            v-model="programSession.academic_max_additional_credits"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.academic_max_additional_credits"
            type="number"
            step="1"
            min="0"
            outer-classes="col-span-1 flex items-center"
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2"
                ><span
                  v-if="!hasProgramSessionTypeInternshipOnly"
                  class="text-red-100 pr-2"
                  >**</span
                >Max</span
              >
            </template>
          </BasicTextInput>
        </div>
        <div v-if="!noDesignatedClasses">
          <BasicCheckbox
            v-model="oneClassAddOn"
            class="my-auto"
            field-label="1 Credit Class Add-on"
            @update:modelValue="updateOneClassAddon"
          />
          <BasicTextInput
            v-if="oneClassAddOn"
            v-model="programSession.one_credit_add_on"
            input-classes="h-10 form-input"
            :vuelidate-instance="v$.one_credit_add_on"
            type="number"
            min="0"
            step="1"
            class="flex-w"
            outer-classes="flex flex-wrap dollar-input items-center mt-2"
            @blur="
              () => updateFieldToDecimals('one_credit_add_on', programSession)
            "
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2">$</span>
            </template>
          </BasicTextInput>
        </div>
        <div v-if="!noDesignatedClasses">
          <BasicCheckbox
            v-model="threeClassAddOn"
            class="my-auto"
            field-label="3 Credit Class Add-on"
            @update:modelValue="updateThreeClassAddon"
          />
          <BasicTextInput
            v-if="threeClassAddOn"
            v-model="programSession.three_credit_add_on"
            input-classes="h-10 form-input"
            :vuelidate-instance="v$.three_credit_add_on"
            type="number"
            min="0"
            step="1"
            outer-classes="flex flex-wrap dollar-input items-center mt-2"
            @blur="
              () => updateFieldToDecimals('three_credit_add_on', programSession)
            "
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2">$</span>
            </template>
          </BasicTextInput>
        </div>
        <CheckboxSelect
          v-if="!noDesignatedClasses"
          v-model="programSession.specific_class_taught_time"
          :vuelidate-instance="v$.specific_class_taught_time"
          field-label="Are classes taught at a specific time?"
          :options="classesTaughtTimesOptions"
          :multiple="true"
          :deselect-from-dropdown="true"
          :close-on-select="false"
          placeholder="Pick a value"
        />
        <CheckboxSelect
          v-if="!noDesignatedClasses"
          v-model="programSession.academics_days_of_week"
          :options="daysOfTheWeekTaught"
          :vuelidate-instance="v$.academics_days_of_week"
          :multiple="true"
          :deselect-from-dropdown="true"
          :close-on-select="false"
          :clear-search-on-blur="() => false"
          field-label="What days of the week are classes taught?"
        />
        <VSelectCaret
          v-if="!noDesignatedClasses"
          v-model="programSession.academic_assessment_type_id"
          :options="assessmentTypes"
          :vuelidate-instance="v$.academic_assessment_type_id"
          field-label="Assessment Type"
          :reduce="(val) => val.id"
        />
        <RadioYesNo
          v-if="!noDesignatedClasses"
          v-model="programSession.academics_has_add_drop_period"
          class="col-span-1 pb-3"
          field-label="Add/drop period?"
        />
        <SearchableSelect
          v-if="!noDesignatedClasses"
          v-model="programSession.transcript_provider"
          :clear-search-on-blur="() => false"
          :deselect-from-dropdown="true"
          :loading="entitiesLoading"
          label="name"
          :options="entitiesOptions.items"
          :vuelidate-instance="v$.transcript_provider"
          field-label="Transcript Provider"
          placeholder="Search and add"
          @search="fetchEntitiesOptions"
        />
        <BasicSwitch
          v-if="!noDesignatedClasses"
          class="col-span-2 border-gray-300 border-t-[1px] pt-4"
          field-label="No designated Language Placement Exams"
          :model-value="noDesignatedLanguagePlacementExam"
          @update:modelValue="onNoDesignatedLanguagePlacementExam"
        />
        <template
          v-if="!noDesignatedLanguagePlacementExam && !noDesignatedClasses"
        >
          <BasicTextInput
            v-model="programSession.language_entry_exam_min_score"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.language_entry_exam_min_score"
            field-label="What score needed to be eligible for program"
          />
          <div>
            <label class="common-label-text mb-2"
              >What is the language exam administered?</label
            >
            <div class="font-normal">
              {{
                programSession?.sponsoring_institutions
                  ?.language_entry_exam_administration || "On Site"
              }}
            </div>
          </div>
          <div>
            <label class="common-label-text mb-2"
              >What's the format of the placement exam?</label
            >
            <div class="font-normal">
              {{
                programSession?.sponsoring_institutions?.assessment_structure ||
                "On Site"
              }}
            </div>
          </div>
          <div>
            <label class="common-label-text mb-2"
              >How is the placement exam administered?</label
            >
            <div class="font-normal">
              {{
                programSession?.sponsoring_institutions
                  ?.language_entry_exam_administration || "On Site"
              }}
            </div>
          </div>
          <div>
            <label class="common-label-text mb-2"
              >Instructions/Expectations of experience</label
            >
            <div class="font-normal">
              {{
                programSession?.sponsoring_institutions
                  ?.language_entry_exam_administration || "On Site"
              }}
            </div>
          </div>
          <BasicTextInput
            v-model="programSession.language_entry_exam_difference"
            outer-classes="col-span-2"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.language_entry_exam_difference"
            field-label="What happens if I test into a different level?"
          />
        </template>
      </div>
    </template>
  </ExpandingSection>
</template>

<style scoped>
.dollar-input :deep(input) {
  width: calc(100% - 18px);
}
</style>
